import ResponsiveContainer from "../components/DensityResponsiveContainer";
import CsvExportButton from "../components/CsvExportButton";
import { viewportHeight as layoutViewportHeight } from "../utils/displayDensity";
/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import Switch from "react-switch";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,

  Tooltip,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Cell,
} from "recharts";

import DateSlider from "../components/DateSlider";
import VirtualizedTable from "../components/VirtualizedTable";
import VirtualizedTikTokGrid from "../components/VirtualizedTikTokGrid"; // re-used; we map search volume -> views
import VirtualizedDropdown from "../components/VirtualizedDropdown/VirtualizedDropdown";
import TextInputField from "../components/Fields/TextInputField";
import SavedViewsManager from "../components/SavedViewsManager";
import { useTikTokData } from "../contexts/TikTokDataContext"; // for metadata + filter catalogs
import { useGlobalData } from "../contexts/DataContext";

/* match grid sizing so header aligns */
const CARD_WIDTH = 200;
const GRID_COL_GAP = 12;

/** --------------------------------
 * helpers
 * -------------------------------- */
const fmtMDY = (v) => {
  const d = new Date(v);
  if (isNaN(d)) return v;
  const m = d.getMonth() + 1;
  const day = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${m}/${day}/${yy}`;
};

const fmtAbbrev = (n) => {
  const abs = Math.abs(n || 0);
  if (abs >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return Math.round(n || 0).toString();
};

// gz fetch helper
async function fetchGzipText(url) {
  const res = await fetch(url, { mode: "cors", cache: "default" });
  if (!res.ok) throw new Error(`${url} HTTP ${res.status}`);
    const enc = (res.headers.get("content-encoding") || "").toLowerCase();
  if (enc.includes("gzip") || enc.includes("br") || enc.includes("deflate")) {
    return (await res.text()).trim();
  }
  if (url.endsWith(".gz") && "DecompressionStream" in window && res.body) {
    try {
      const ds = new DecompressionStream("gzip");
      const reader = res.body.pipeThrough(ds).getReader();
      const chunks = [];
      for (;;) { const { value, done } = await reader.read(); if (done) break; if (value) chunks.push(value); }
      return await new Blob(chunks, { type: "text/plain;charset=utf-8" }).text();
    } catch { /* fall through */ }
  }
  if (url.endsWith(".gz")) {
    const buf = await res.arrayBuffer();
    const u8 = new Uint8Array(buf);
    const looksGzip = u8.length >= 2 && u8[0] === 0x1f && u8[1] === 0x8b;
    if (looksGzip) {
      try {
        const { default: pako } = await import("pako");
        return pako.ungzip(u8, { to: "string" }).trim();
      } catch { /* fallback below */ }
    }
    return new TextDecoder("utf-8").decode(u8).trim();
  }
  return (await res.text()).trim();
}

// number of weekly periods to keep per pill
const WINDOW_PERIODS = {
  "7d": 1,     // last 1 weekly period (7 days)
  "1mo": 4,    // last 4 weeks
  "3mo": 12,   // last 12 weeks
  "1yr": 52,   // last 52 weeks
  "3yr": 156,  // last 156 weeks
  lifetime: Infinity,
};

function Search() {
  // --------- filters / UI state (kept identical to TikTok.jsx so SavedViews works) ---------
  const [games, setGames] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedPublishers, setSelectedPublishers] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedDevelopers, setSelectedDevelopers] = useState([]);
  const [selectedLiveService, setSelectedLiveService] = useState(null);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [selectedUGC, setSelectedUGC] = useState(null);
  const [selectedFocus, setSelectedFocus] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);
  const [sortMode, setSortMode] = useState("views"); // we map Search Volume -> views
  const [onlyUnreleased, setOnlyUnreleased] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [comparisonGames, setComparisonGames] = useState([]); // <-- chart selection
  const [useLogScale, setUseLogScale] = useState(false);
  const [chartLarge, setChartLarge] = useState(false);
  const [showTableView, setShowTableView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [skeletonRowCount, setSkeletonRowCount] = useState(30);
  const [hiddenGames, setHiddenGames] = useState([]);

  // ── NEW: Add Games picker state ──────────────────────────────────────────────
  const [addQuery, setAddQuery] = useState("");
  const [addFocusIdx, setAddFocusIdx] = useState(0);

  // New: search window (weekly metric)
  // "7d" | "1mo" | "3mo" | "1yr" | "3yr" | "lifetime"
  const [windowKey, setWindowKey] = useState("lifetime");

  const { metaData, loading: metaLoading,
    getAllTags, getAllPublishers, getAllGenres, getAllDevelopers,
    getAllLiveServiceOptions, getAllPricingOptions, getAllUGCOptions,
    getAllFocusOptions, getAllPlatformGroups, getAllReleaseDates,
    allTags, allPublishers, allGenres, allDevelopers,
    allLiveService, allPricing, allUGC, allFocus, allPlatforms,
    releaseDateRange
  } = useTikTokData();

  const { metadata: steamMetadata, latestCCU, loading: globalSteamLoading } = useGlobalData();

// Convert a date-like value to YYYY-MM-DD (or null if invalid)
const toISODate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

  // ---------- load weekly Search Volume time series from game_stats.csv.gz ----------
  useEffect(() => {
    const load = async () => {
      if (metaLoading || globalSteamLoading) return;
      if (!Array.isArray(metaData) || metaData.length === 0) return;

      setLoading(true);
      try {
        const text = await fetchGzipText(
          "https://d1ct3zodbaiefe.cloudfront.net/game_stats.csv.gz"
        );
        const rows = d3.csvParse(text.trim());

        // map metadata for cards
        const metaMap = new Map();
        metaData.forEach((m) => {
          const k = m.Game?.trim();
          if (k) metaMap.set(k, m);
        });

        // group weekly search series by game
        const grouped = new Map(); // game -> [{date, search_volume}]
        for (const r of rows) {
          const game = r.Game?.trim();
          if (!game) continue;
          const m = metaMap.get(game);
          if (!m || !m.ImageURL) continue; // skip if we can’t render card

          const sv = parseFloat(r.Search_Volume);
          const period = new Date(r.Period);
          if (!Number.isFinite(sv) || isNaN(period.getTime())) continue;

          if (!grouped.has(game)) grouped.set(game, []);
          grouped.get(game).push({
            date: new Date(period.toISOString().slice(0, 10)), // YYYY-MM-DD
            sv,
          });
        }

        // Sort series, compute latest/prev + weekly deltas, and shape card rows
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const combined = [];
        for (const [game, series] of grouped.entries()) {
          series.sort((a, b) => a.date - b.date);
          if (series.length === 0) continue;

          const latest = series[series.length - 1];
          const prev = series[series.length - 2] || { sv: 0 };

          // weekly delta (last period vs previous period)
          const delta7d = latest.sv - (prev?.sv || 0);
          const pct7d = (prev?.sv || 0) > 0 ? (delta7d / prev.sv) * 100 : 0;

          const meta = metaMap.get(game);
          const rdISO = meta?.Release_Date ? toISODate(meta.Release_Date) : null;
          const rd = rdISO ? new Date(rdISO) : null;
          const isUnreleased = rd ? rd > today : true;

          combined.push({
            name: game,
            imageUrl: meta?.ImageURL || "",
            publisher: meta?.Publisher?.trim() || "",
            tags: meta?.Tags?.split(",").map((t) => t.trim()).filter(Boolean) || [],
            genres: meta?.Genre?.split(",").map((g) => g.trim()).filter(Boolean) || [],
            developer: meta?.Developer?.trim() || "",
            live_service_static: meta?.Live_Service_Static?.trim() || "",
            pricing: meta?.Pricing_Monetization?.trim() || "",
            user_generated_content: meta?.["User_Generated_Content_(UGC)"]?.trim() || "",
            single_multiplayer_focus: meta?.Single_Multiplayer_Focus?.trim() || "",
            platforms: meta?.Platform_Group_Filter?.split(",").map((p) => p.trim()).filter(Boolean) || [],
            releaseStatus: meta?.Released_Unreleased || "",
            release_date: rdISO,
            isUnreleased,

            // core metric mapping (so grids/tables work as-is):
            views: Number(latest.sv) || 0,             // <- Search Volume (latest)
            videos: 0,                                  // not used; keep for API parity
            viewGrowth_7d: Number(delta7d) || 0,       // last week delta
            percentGrowth_7d: Number(pct7d) || 0,

            // long series for charts
            history: series.map((pt, idx) => ({
              date: pt.date.toISOString().slice(0, 10),
              // delta vs previous weekly point
              deltaViews: idx > 0 ? pt.sv - series[idx - 1].sv : 0,
              views: pt.sv,
            })),
          });
        }

        setGames(combined);

        // warm filter catalogs
        getAllTags();
        getAllPublishers();
        getAllGenres();
        getAllDevelopers();
        getAllLiveServiceOptions();
        getAllPricingOptions();
        getAllUGCOptions();
        getAllFocusOptions();
        getAllPlatformGroups();
        getAllReleaseDates();
      } catch (e) {
        console.error("❌ Failed to load Search Volume:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [metaData, metaLoading, globalSteamLoading]);

  // skeleton rows sized to viewport
  useEffect(() => {
    const rowHeight = 56;
    const extraHeight = 200;
    const rows = Math.ceil((layoutViewportHeight() + extraHeight) / rowHeight);
    setSkeletonRowCount(rows);
  }, []);

  const handleRemove = (name) => setHiddenGames((prev) => [...prev, name]);
  const handleReset = () => {
    setHiddenGames([]);
    setSelectedTags([]);
    setSelectedPublishers([]);
    setSelectedGenres([]);
    setSelectedDevelopers([]);
    setSelectedLiveService(null);
    setSelectedPricing(null);
    setSelectedUGC(null);
    setSelectedFocus([]);
    setSelectedPlatforms([]);
    setSelectedDateRange([null, null]);
    setSortMode("views");
    setOnlyUnreleased(false);
    setWindowKey("lifetime");
    setComparisonGames([]); // <-- reset selected chart games
    setAddQuery("");
  };

  // ------------- filtering & sorting (unchanged except fields) -------------
  const filtered = useMemo(() => {
    let result = games
      .filter((g) => !hiddenGames.includes(g.name))
      .filter((g) => g.name.toLowerCase().includes((searchTerm || "").toLowerCase()))
      .filter(
        (g) =>
          !selectedTags.length ||
          selectedTags.some((t) => g.tags.includes(t.value))
      )
      .filter(
        (g) =>
          !selectedPublishers.length ||
          selectedPublishers.some((p) => g.publisher === p.value)
      )
      .filter(
        (g) =>
          !selectedGenres.length ||
          selectedGenres.some((x) => g.genres?.includes(x.value))
      )
      .filter(
        (g) =>
          !selectedDevelopers.length ||
          selectedDevelopers.some((d) => g.developer === d.value)
      )
      .filter(
        (g) =>
          !selectedLiveService ||
          g.live_service_static === selectedLiveService.value
      )
      .filter((g) => !selectedPricing || g.pricing === selectedPricing.value)
      .filter(
        (g) => !selectedUGC || g.user_generated_content === selectedUGC.value
      )
      .filter((g) => {
        if (!selectedDateRange[0] || !selectedDateRange[1]) return true;
        if (!g.release_date) return onlyUnreleased ? true : false;
        const rd = new Date(g.release_date);
        if (isNaN(rd)) return onlyUnreleased ? true : false;
        const t = rd.getTime();
        return (
          t >= selectedDateRange[0].getTime() &&
          t <= selectedDateRange[1].getTime()
        );
      });

    if (onlyUnreleased) result = result.filter((g) => g.isUnreleased);

    // sort
    result.sort((a, b) => {
      if (windowKey === "7d") {
        // sort by last-week delta
        return (b.viewGrowth_7d || 0) - (a.viewGrowth_7d || 0);
      }
      if (sortMode === "views") return (b.views || 0) - (a.views || 0);
      if (sortMode === "percentGrowth")
        return (b.percentGrowth_7d || -Infinity) - (a.percentGrowth_7d || -Infinity);
      return (b.views || 0) - (a.views || 0);
    });

    return result;
  }, [
    games,
    hiddenGames,
    searchTerm,
    selectedTags,
    selectedPublishers,
    selectedGenres,
    selectedDevelopers,
    selectedLiveService,
    selectedPricing,
    selectedUGC,
    selectedFocus,
    selectedPlatforms,
    selectedDateRange,
    onlyUnreleased,
    sortMode,
    windowKey,
  ]);

  // What goes into cards/table (map to TikTok grid fields)
  const displayed = useMemo(() => {
    if (windowKey === "7d") {
      // show last-week delta
      return filtered.map((g) => ({
        ...g,
        views: g.viewGrowth_7d || 0,      // bar value
        videos: 0,
        percentGrowth: g.percentGrowth_7d || 0,
        isLifetime: false,
      }));
    }
    // other windows still display latest weekly value on the cards
    return filtered.map((g) => ({
      ...g,
      views: g.views || 0,
      videos: 0,
      isLifetime: windowKey === "lifetime",
    }));
  }, [filtered, windowKey]);

  // -------- chart data -----------
  const lineChartData = useMemo(() => {
    if (windowKey === "7d") return [];
    const keep = WINDOW_PERIODS[windowKey] ?? Infinity;
    const dateMap = {};
    const targets = comparisonGames.length ? comparisonGames : null;

    if (targets) {
      targets.forEach((name) => {
        const g = games.find((x) => x.name === name);
        if (!g) return;
        g.history.forEach((h) => {
          if (!dateMap[h.date]) dateMap[h.date] = { date: h.date };
          dateMap[h.date][g.name] = h.views; // weekly value
        });
      });
    } else {
      games.forEach((g) => {
        g.history.forEach((h) => {
          if (!dateMap[h.date]) dateMap[h.date] = { date: h.date };
          dateMap[h.date].Total = (dateMap[h.date].Total || 0) + h.views;
        });
      });
    }

    let rows = Object.values(dateMap).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
    if (keep !== Infinity && rows.length > keep) {
      rows = rows.slice(rows.length - keep);
    }
    return rows;
  }, [games, comparisonGames, windowKey]);

  // For 7d window: one-row bar chart of last-week deltas
  const barChartData = useMemo(() => {
    if (windowKey !== "7d") return { data: [], items: [], latestISO: null };
    let targets =
      comparisonGames.length > 0 ? [...comparisonGames] : games.map((g) => g.name);

    const items = targets
      .map((name) => {
        const g = games.find((x) => x.name === name);
        if (!g) return null;
        return { name, value: g.viewGrowth_7d || 0 };
      })
      .filter(Boolean)
      .sort((a, b) => b.value - a.value)
      .slice(0, comparisonGames.length ? undefined : 12)
      .map((t, idx) => ({
        ...t,
        color: ["#3fa9f5", "#ffcc00", "#00cc99", "#ff66cc", "#66ccff"][idx % 5],
      }));

    const row = items.reduce((acc, it) => ((acc[it.name] = it.value), acc), {});
    let latestISO = null;
    games.forEach((g) => {
      const h = g.history;
      if (h?.length) {
        const d = h[h.length - 1].date;
        if (!latestISO || new Date(d) > new Date(latestISO)) latestISO = d;
      }
    });
    return { data: [row], items, latestISO };
  }, [games, comparisonGames, windowKey]);

  // y-max for chart scaling
  const yMax = useMemo(() => {
    if (windowKey === "7d") {
      return Math.max(0, ...barChartData.items.map((it) => Number(it.value || 0)));
    }
    const names = comparisonGames.length ? comparisonGames : ["Total"];
    let max = 0;
    for (const r of lineChartData) {
      for (const n of names) {
        const v = r[n];
        if (typeof v === "number" && v > max) max = v;
      }
    }
    return max;
  }, [windowKey, barChartData, lineChartData, comparisonGames]);

  const avgLine = useMemo(() => {
    if (windowKey === "7d") return null;
    if (comparisonGames.length === 0) return null;
    const all = lineChartData.flatMap((row) =>
      comparisonGames.map((name) => row[name]).filter((v) => typeof v === "number")
    );
    const avg = all.reduce((a, b) => a + b, 0) / (all.length || 1);
    return avg;
  }, [lineChartData, comparisonGames, windowKey]);

  const seriesColor = (i) =>
    ["#3fa9f5", "#ffcc00", "#00cc99", "#ff66cc", "#66ccff"][i % 5];

  const labelPoints = useMemo(() => {
    if (windowKey === "7d") return [];
    const names = comparisonGames.length ? comparisonGames : ["Total"];
    return names
      .map((name, idx) => {
        let max = -Infinity, maxRow = null;
        for (const row of lineChartData) {
          const v = row[name];
          if (typeof v === "number" && v > max) {
            max = v; maxRow = row;
          }
        }
        if (!maxRow || !isFinite(max)) return null;
        return {
          name,
          x: maxRow.date,
          y: max,
          color: comparisonGames.length ? seriesColor(idx) : "#3fa9f5",
          key: `${name}-${maxRow.date}`,
        };
      })
      .filter(Boolean);
  }, [lineChartData, comparisonGames, windowKey]);

  const totalSearch = useMemo(
    () => displayed.reduce((s, g) => s + (g.views || 0), 0),
    [displayed]
  );

  // align "Time Period" header with centered grid
  const [gridLeftPad, setGridLeftPad] = useState(0);
  const gridHostRef = useRef(null);
  useEffect(() => {
    const computeLeft = () => {
      const host = gridHostRef.current;
      if (!host) return;
      const w = host.clientWidth || 0;
      const cols = Math.max(1, Math.floor((w + GRID_COL_GAP) / (CARD_WIDTH + GRID_COL_GAP)));
      const gridW = cols * CARD_WIDTH + Math.max(0, cols - 1) * GRID_COL_GAP;
      const offset = Math.max(0, Math.floor((w - gridW) / 2));
      setGridLeftPad(offset);
    };
    computeLeft();
    window.addEventListener("resize", computeLeft);
    return () => window.removeEventListener("resize", computeLeft);
  }, []);

  // ------------- UI bits reused -------------
  const selectLikeBtn = {
    width: "100%",
    height: 38,
    minHeight: 38,
    background: "#111",
    color: "#cfd8e3",
    border: "1px solid #444",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  };

  const pill = (val, label) => (
    <button
      key={val}
      onClick={() => setWindowKey(val)}
      style={{
        padding: "6px 10px",
        fontSize: 13,
        fontWeight: 700,
        borderRadius: 6,
        border: "1px solid " + (windowKey === val ? "#3fa9f5" : "#444"),
        background: windowKey === val ? "#3fa9f5" : "transparent",
        color: windowKey === val ? "#000" : "#cfd8e3",
        cursor: "pointer",
      }}
      aria-pressed={windowKey === val}
    >
      {label}
    </button>
  );

  const chartMargins = { left: 66, right: 130, top: 22, bottom: 0 };

  // export
  const exportCSV = () => {
    const rows = displayed.map((g) => ({
      Name: g.name,
      Search_Volume: g.views,
      "%_7D": g.percentGrowth ?? g.percentGrowth_7d ?? "",
      Publisher: g.publisher ?? "",
      Release_Date: g.release_date ?? "",
    }));
    const headers = rows.length
      ? Object.keys(rows[0])
      : ["Name", "Search_Volume", "%_7D", "Publisher", "Release_Date"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv =
      "\uFEFF" +
      [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join(
        "\r\n"
      );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "search_volume_export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // legend (click to remove)
  const LegendContent = ({ payload }) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, paddingTop: 6, justifyContent: "center" }}>
      {payload?.map((item, i) => {
        const name = item.value;
        const color = item.color || item.payload?.stroke || item.payload?.fill;
        return (
          <span
            key={i}
            onClick={() => {
              if (name === "Total") return;
              setComparisonGames((prev) => prev.filter((g) => g !== name));
            }}
            title={name === "Total" ? "" : "Click to remove from chart"}
            style={{
              cursor: name === "Total" ? "default" : "pointer",
              color: "#fff",
              fontWeight: 600,
              fontSize: 12,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              opacity: name === "Total" ? 0.85 : 1,
            }}
          >
            <span
              style={{ width: 10, height: 10, background: color || "#888", display: "inline-block", borderRadius: 2 }}
            />
            {name}
          </span>
        );
      })}
    </div>
  );

  // ── NEW: Add Games derived list (search over all loaded games) ───────────────
  const allGameNames = useMemo(() => games.map((g) => g.name), [games]);

  const addMatches = useMemo(() => {
    const q = (addQuery || "").trim().toLowerCase();
    if (!q) return [];
    // prefix first, then substring; cap for perf
    const pref = [];
    const sub = [];
    for (const name of allGameNames) {
      const lc = name.toLowerCase();
      if (lc.startsWith(q)) pref.push(name);
      else if (lc.includes(q)) sub.push(name);
      if (pref.length + sub.length >= 50) break;
    }
    return [...pref, ...sub].slice(0, 50);
  }, [addQuery, allGameNames]);

  const handleAddGame = (name) => {
    if (!name) return;
    setComparisonGames((prev) => {
      if (prev.includes(name)) return prev;
      return [...prev, name];
    });
    // ensure user sees it immediately
    setShowInsights(true);
  };

  const onAddKeyDown = (e) => {
    if (!addMatches.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAddFocusIdx((i) => Math.min(i + 1, addMatches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAddFocusIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleAddGame(addMatches[addFocusIdx]);
    }
  };

  return (
    <div style={{ display: "flex", background: "#000", color: "#fff", minHeight: "calc(var(--mg-vh) * 100)" }}>
      {/* Sidebar */}
      <div
        style={{
          width: 260,
          padding: 20,
          background: "#111",
          borderRight: "1px solid #333",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <img
          src={`${process.env.PUBLIC_URL}/tiktok-sidebar.png`}
          alt="mindSHARE"
          style={{ width: 180, height: "auto", display: "block", marginBottom: 10 }}
        />

        <SavedViewsManager
          type="search"
          currentFilters={{
            searchTerm,
            tags: selectedTags,
            publishers: selectedPublishers,
            genres: selectedGenres,
            developers: selectedDevelopers,
            liveService: selectedLiveService,
            pricing: selectedPricing,
            ugc: selectedUGC,
            focus: selectedFocus,
            platforms: selectedPlatforms,
            dateRange: selectedDateRange,
            sortMode,
            onlyUnreleased,
            windowKey,
            showInsights,
            showTableView,
            useLogScale,
            chartLarge,
            // NEW: persist selected chart games
            comparisonGames,
          }}
          onApplyFilters={(f) => {
            setSearchTerm(f.searchTerm ?? "");
            setSelectedTags(f.tags ?? []);
            setSelectedPublishers(f.publishers ?? []);
            setSelectedGenres(f.genres ?? []);
            setSelectedDevelopers(f.developers ?? []);
            setSelectedLiveService(f.liveService ?? null);
            setSelectedPricing(f.pricing ?? null);
            setSelectedUGC(f.ugc ?? null);
            setSelectedFocus(f.focus ?? []);
            setSelectedPlatforms(f.platforms ?? []);
            setSortMode(f.sortMode ?? "views");
            setOnlyUnreleased(f.onlyUnreleased ?? false);
            setWindowKey(f.windowKey ?? "lifetime");
            setShowInsights(f.showInsights ?? false);
            setShowTableView(f.showTableView ?? false);
            setUseLogScale(f.useLogScale ?? false);
            setChartLarge(f.chartLarge ?? false);
            setComparisonGames(f.comparisonGames ?? []); // NEW

            const dateRange = f.dateRange ?? [null, null];
            if (dateRange[0] && typeof dateRange[0] === "string") dateRange[0] = new Date(dateRange[0]);
            if (dateRange[1] && typeof dateRange[1] === "string") dateRange[1] = new Date(dateRange[1]);
            setSelectedDateRange(dateRange);
          }}
          onClearFilters={() => {
            setSearchTerm("");
            setSelectedTags([]);
            setSelectedPublishers([]);
            setSelectedGenres([]);
            setSelectedDevelopers([]);
            setSelectedLiveService(null);
            setSelectedPricing(null);
            setSelectedUGC(null);
            setSelectedFocus([]);
            setSelectedPlatforms([]);
            setSelectedDateRange([null, null]);
            setSortMode("views");
            setOnlyUnreleased(false);
            setWindowKey("lifetime");
            setShowInsights(false);
            setShowTableView(false);
            setUseLogScale(false);
            setChartLarge(false);
            setComparisonGames([]); // NEW
            setAddQuery("");
          }}
        />

        <TextInputField
          placeholder="Search Games..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {/* ── NEW: Add Games to Chart ───────────────────────────────────────── */}
        <div
          style={{
            marginTop: 6,
            padding: 10,
            background: "#0e0e0e",
            border: "1px solid #333",
            borderRadius: 8,
          }}
        >
          <div style={{ fontSize: 12, color: "#9fb4c7", fontWeight: 700, marginBottom: 6 }}>
            Add Games (to chart)
          </div>
          <input
            type="text"
            value={addQuery}
            onChange={(e) => {
              setAddQuery(e.target.value);
              setAddFocusIdx(0);
            }}
            onKeyDown={onAddKeyDown}
            placeholder="Type to search… e.g., FIFA"
            style={{
              width: "100%",
              height: 36,
              padding: "0 10px",
              background: "#161616",
              color: "#e6eef6",
              border: "1px solid #333",
              borderRadius: 6,
              outline: "none",
              fontSize: 13,
            }}
          />
          {!!addQuery && (
            <div
              style={{
                marginTop: 8,
                maxHeight: 220,
                overflowY: "auto",
                borderTop: "1px solid #222",
              }}
            >
              {addMatches.length === 0 ? (
                <div style={{ padding: "8px 4px", color: "#777", fontSize: 12 }}>
                  No matches
                </div>
              ) : (
                addMatches.map((name, idx) => {
                  const already = comparisonGames.includes(name);
                  const focused = idx === addFocusIdx;
                  return (
                    <div
                      key={name}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 10,
                        padding: "6px 6px",
                        background: focused ? "#1a1f26" : "transparent",
                        borderRadius: 6,
                      }}
                      onMouseEnter={() => setAddFocusIdx(idx)}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          color: "#dfe8f1",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          flex: 1,
                        }}
                        title={name}
                      >
                        {name}
                      </span>
                      <button
                        onClick={() => handleAddGame(name)}
                        disabled={already}
                        title={already ? "Already in chart" : "Add to chart"}
                        style={{
                          minWidth: 26,
                          height: 26,
                          borderRadius: 6,
                          border: "1px solid #2b3b4a",
                          background: already ? "#1d2a34" : "#13212c",
                          color: already ? "#86a3b8" : "#9bd1ff",
                          fontWeight: 900,
                          cursor: already ? "default" : "pointer",
                          lineHeight: "1",
                        }}
                      >
                        {already ? "✓" : "+"}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
          {comparisonGames.length > 0 && (
            <button
              onClick={() => setShowInsights(true)}
              style={{ ...selectLikeBtn, marginTop: 8 }}
            >
              📈 Open Chart
            </button>
          )}
        </div>
        {/* ──────────────────────────────────────────────────────────────────── */}

        <VirtualizedDropdown
          placeholder="Sort by Search Volume"
          value={
            [
              { label: "Sort by Search Volume", value: "views" },
              { label: "Sort by % 7D Growth", value: "percentGrowth" },
            ].find((o) => o.value === sortMode) || { label: "Sort by Search Volume", value: "views" }
          }
          onChange={(opt) => setSortMode(opt?.value || "views")}
          options={[
            { label: "Sort by Search Volume", value: "views" },
            { label: "Sort by % 7D Growth", value: "percentGrowth" },
          ]}
          isClearable={false}
        />

        <DateSlider
          range={releaseDateRange}
          selected={selectedDateRange}
          onChange={setSelectedDateRange}
          onClear={() => setSelectedDateRange([null, null])}
        />

        <label style={{ fontSize: 14, color: "#ccc", marginTop: 4, marginBottom: 12, display: "flex", alignItems: "center" }}>
          <input type="checkbox" checked={onlyUnreleased} onChange={(e) => setOnlyUnreleased(e.target.checked)} style={{ marginRight: 6 }} />
          Show Only Unreleased
        </label>

        {/* same filter stack as TikTok */}
        <VirtualizedDropdown placeholder="All Tags" value={selectedTags} onChange={setSelectedTags} options={allTags.map((t) => ({ label: t, value: t }))} isClearable isMulti />
        <VirtualizedDropdown placeholder="All Genres" value={selectedGenres} onChange={setSelectedGenres} options={allGenres.map((p) => ({ label: p, value: p }))} isClearable isMulti />
        <VirtualizedDropdown placeholder="All Publishers" value={selectedPublishers} onChange={setSelectedPublishers} options={allPublishers.map((p) => ({ label: p, value: p }))} isClearable isMulti />
        <VirtualizedDropdown placeholder="All Developers" value={selectedDevelopers} onChange={setSelectedDevelopers} options={allDevelopers.map((d) => ({ label: d, value: d }))} isClearable isMulti />
        <VirtualizedDropdown placeholder="Business Model" value={selectedLiveService} onChange={setSelectedLiveService} options={allLiveService.map((s) => ({ label: s, value: s }))} isClearable />
        <VirtualizedDropdown placeholder="Pricing" value={selectedPricing} onChange={setSelectedPricing} options={allPricing.map((p) => ({ label: p, value: p }))} isClearable />
        <VirtualizedDropdown placeholder="UGC" value={selectedUGC} onChange={setSelectedUGC} options={allUGC.map((ugc) => ({ label: ugc, value: ugc }))} isClearable />
        <VirtualizedDropdown placeholder="Play Type" value={selectedFocus} onChange={setSelectedFocus} options={allFocus.map((f) => ({ label: f, value: f }))} isClearable isMulti />
        <VirtualizedDropdown placeholder="Platform Type" value={selectedPlatforms} onChange={setSelectedPlatforms} options={allPlatforms.map((p) => ({ label: p, value: p }))} isClearable isMulti />

        <button onClick={() => setShowTableView(!showTableView)} style={selectLikeBtn}>
          {showTableView ? "🖼️ Card View" : "📋 Table View"}
        </button>

        <button onClick={handleReset} style={selectLikeBtn}>Reset Games</button>
        <CsvExportButton onClick={exportCSV} style={selectLikeBtn}>⬇ Export CSV</CsvExportButton>
      </div>

      {/* Main */}
      <div style={{ flex: 1, padding: "20px 30px 30px 30px" }}>
        <div ref={gridHostRef}>
          {showInsights && (
            <div
              style={{
                marginTop: 0,
                marginBottom: 16,
                padding: "10px 20px 20px 20px",
                background: "#111",
                border: "1px solid #333",
                borderRadius: 10,
              }}
            >
              {/* Title + controls */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h2 style={{ color: "#3fa9f5", fontSize: 16, lineHeight: "20px", margin: 0 }}>
                  {comparisonGames.length > 0 ? "Search Volume (Weekly)" : "Total Search Volume (Weekly)"}
                </h2>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <label style={{ fontSize: 14, color: "#ccc" }}>
                    <input type="checkbox" checked={useLogScale} onChange={() => setUseLogScale(!useLogScale)} style={{ marginRight: 6 }} />
                    Log Scale
                  </label>
                  <label style={{ fontSize: 14, color: "#ccc" }}>
                    <input type="checkbox" checked={chartLarge} onChange={() => setChartLarge(!chartLarge)} style={{ marginRight: 6 }} />
                    Larger Chart
                  </label>
                  <button
                    onClick={() => setComparisonGames([])}
                    style={{ background: "#333", color: "#fff", border: "1px solid #555", padding: "6px 10px", fontSize: 12, borderRadius: 6, cursor: "pointer" }}
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => setShowInsights(false)}
                    style={{ background: "#222", color: "#fff", border: "1px solid #444", padding: "6px 10px", fontSize: 12, borderRadius: 6, cursor: "pointer" }}
                  >
                    Close
                  </button>
                </div>
              </div>

              <div style={{ height: chartLarge ? 660 : 330, borderRadius: 8, overflow: "hidden", backgroundColor: "#111" }}>
                <ResponsiveContainer width="100%" height="100%">
                  {windowKey === "7d" ? (
                    <BarChart data={barChartData.data} margin={{ left: 66, right: 130, top: 22, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                      <XAxis dataKey={() => ""} tick={false} axisLine={false} />
                      <YAxis
                        width={66}
                        tickFormatter={(v) => v.toLocaleString()}
                        stroke="#fff"
                      />
                      <Legend
                        payload={barChartData.items.map((it) => ({ value: it.name, color: it.color, type: "square" }))}
                        content={LegendContent}
                      />
                      {barChartData.items.map((it) => (
                        <Bar key={it.name} dataKey={it.name} name={it.name} isAnimationActive animationDuration={650} animationEasing="ease-out" radius={[6, 6, 0, 0]}>
                          <Cell fill={it.color} />
                        </Bar>
                      ))}
                    </BarChart>
                  ) : (
                    <LineChart data={lineChartData} margin={chartMargins}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                      <XAxis
                        dataKey="date"
                        stroke="#888"
                        padding={{ left: 10, right: 90 }}
                        tickFormatter={fmtMDY}
                        tick={{ fontWeight: "bold", fontSize: 13, fontFamily: "sans-serif" }}
                      />
                      <YAxis
                        width={66}
                        scale={useLogScale ? "log" : "linear"}
                        domain={
                          useLogScale
                            ? [
                                (dataMin) => Math.max(1, Math.floor(Math.min(dataMin ?? yMax, yMax) * 0.98)),
                                (dataMax) => Math.ceil(Math.max(dataMax ?? yMax, yMax) * 1.22),
                              ]
                            : [
                                (dataMin) => Math.max(0, Math.floor((dataMin ?? 0) * 0.98)),
                                (dataMax) => Math.ceil((dataMax ?? yMax) * 1.22),
                              ]
                        }
                        tickFormatter={(v) => v.toLocaleString()}
                        stroke="#fff"
                        tick={{ fontWeight: "bold", fontSize: 13, fontFamily: "sans-serif" }}
                      />
                      <Tooltip
                        content={({ label, payload }) => (
                          <div style={{ background: "#222", border: "1px solid #555", padding: 10, color: "#fff", fontSize: 13 }}>
                            <strong>{fmtMDY(label)}</strong>
                            {[...(payload || [])]
                              .sort((a, b) => (b.value || 0) - (a.value || 0))
                              .map((entry, i) => (
                                <div key={i} style={{ color: entry.color }}>
                                  {entry.name}: {entry.value?.toLocaleString()}
                                </div>
                              ))}
                          </div>
                        )}
                      />
                      <Legend
                        content={LegendContent}
                        payload={
                          comparisonGames.length
                            ? comparisonGames.map((name, idx) => ({ value: name, type: "line", color: seriesColor(idx) }))
                            : [{ value: "Total", type: "line", color: "#3fa9f5" }]
                        }
                      />
                      {comparisonGames.length === 0 ? (
                        <Line type="monotone" dataKey="Total" stroke="#3fa9f5" strokeWidth={2} dot={false} />
                      ) : (
                        comparisonGames.map((name, idx) => (
                          <Line key={name} type="monotone" dataKey={name} stroke={seriesColor(idx)} strokeWidth={2} dot={false} />
                        ))
                      )}

                      {avgLine && (
                        <ReferenceLine
                          y={avgLine}
                          stroke="#aaa"
                          strokeDasharray="5 5"
                          label={{ value: `Avg: ${fmtAbbrev(avgLine)}`, fill: "#ccc", position: "right", fontSize: 12, fontWeight: 400 }}
                        />
                      )}
                      {labelPoints.map((p) => (
                        <ReferenceDot
                          key={p.key}
                          x={p.x}
                          y={p.y}
                          r={3}
                          isFront
                          fill={p.color}
                          stroke="none"
                          label={{ value: p.name, position: "top", fill: p.color, fontSize: 12, fontWeight: 700 }}
                        />
                      ))}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Time Period row */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, paddingLeft: gridLeftPad }}>
            <span style={{ fontWeight: "bold", color: "#ccc", fontSize: 16 }}>Time Period:</span>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {pill("7d", "7D")}
              {pill("1mo", "1MO")}
              {pill("3mo", "3MO")}
              {pill("1yr", "1YR")}
              {pill("3yr", "3YR")}
              {pill("lifetime", "Lifetime")}
            </div>
          </div>

          {showTableView ? (
            <VirtualizedTable
              globalLoading={metaLoading}
              loading={loading}
              skeletonRowCount={skeletonRowCount}
              filtered={displayed}
              totalViews={totalSearch}
              comparisonGames={comparisonGames}
              setComparisonGames={setComparisonGames}
              setShowInsights={setShowInsights}
            />
          ) : (
            <VirtualizedTikTokGrid
              games={displayed}
              loading={metaLoading || loading}
              setShowInsights={setShowInsights}
              comparisonGames={comparisonGames}
              setComparisonGames={setComparisonGames}
              onRemoveGame={handleRemove}
              // NOTE: this grid labels the metric as "Views". If you want it to say "Search",
              // add a prop to the component (e.g., metricLabel="Search Volume") and forward it inside.
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Search;
